import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/Modal';
import { Button } from '../../components/Button';

// ============================================================================
// 定数
// ============================================================================

const STORAGE_KEYS = {
    TUTORIAL_STEP: 'tutorial_global_step',
    TUTORIAL_STARTED: 'tutorial_started',
} as const;

const Z_INDEX = {
    OVERLAY: 9998,
    TOOLTIP: 9999,
    DROPDOWN_MAX: 2147483647,
} as const;

const STYLE = {
    HIGHLIGHT_PADDING: 4,
    TOOLTIP_OFFSET: 20,
    // パネルと区別するために薄いクリーム色に変更
    TOOLTIP_BG_COLOR: '#fffaf0',
    BORDER_COLOR: 'rgba(255, 215, 0, 1)',
} as const;

const MOBILE_BREAKPOINT = 768;

// 分析結果パネルのキー（検索パネル以外）
const RESULT_PANEL_KEYS = [
    'anTotalTime', 'anAverageTime', 'anTotalSchedule',
    'anRanking', 'anChart', 'anTable', 'anAd'
] as const;

// ============================================================================
// 型定義
// ============================================================================

type PageType = 'desk' | 'analysis' | 'sidebar';
type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TutorialStep {
    id: string;
    title: string;
    description: string;
    targetId: string;
    mobileTargetId?: string;
    page: PageType;
    position?: TooltipPosition;
}

interface FirstTimeExperienceTutorialProps {
    isOpen: boolean;
    onClose: () => void;
}

interface RectLike {
    top: number;
    left: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
}

// ============================================================================
// カスタムフック
// ============================================================================

/**
 * ウィンドウサイズを監視するカスタムフック
 */
const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};

/**
 * チュートリアルステップをセッションストレージで管理するフック
 */
const useTutorialStep = () => {
    const [globalStep, setGlobalStep] = useState<number>(() => {
        const saved = sessionStorage.getItem(STORAGE_KEYS.TUTORIAL_STEP);
        return saved ? parseInt(saved, 10) : 0;
    });

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.TUTORIAL_STEP, String(globalStep));
    }, [globalStep]);

    return [globalStep, setGlobalStep] as const;
};

// ============================================================================
// ヘルパー関数
// ============================================================================

/**
 * 複数要素を囲む最小の矩形を計算
 */
const calculateBoundingRect = (selectors: string[]): RectLike | null => {
    let minLeft = Infinity;
    let minTop = Infinity;
    let maxRight = -Infinity;
    let maxBottom = -Infinity;
    let found = false;

    selectors.forEach(selector => {
        const el = document.querySelector(selector);
        if (el) {
            const r = el.getBoundingClientRect();
            if (r.width > 0 && r.height > 0) {
                minLeft = Math.min(minLeft, r.left);
                minTop = Math.min(minTop, r.top);
                maxRight = Math.max(maxRight, r.right);
                maxBottom = Math.max(maxBottom, r.bottom);
                found = true;
            }
        }
    });

    if (!found) return null;

    return {
        top: minTop,
        left: minLeft,
        right: maxRight,
        bottom: maxBottom,
        width: maxRight - minLeft,
        height: maxBottom - minTop,
    };
};

/**
 * ツールチップの位置情報を計算
 */
const calculateTooltipPosition = (
    positionRect: RectLike,
    stepId: string,
    isMobile: boolean
): { left: number; top: number; transform: string; width?: string; arrowDirection?: 'down' | 'up' | 'left' } => {
    const isSimpleAnalytics = stepId === 'tutorial-desk-simple-analytics';
    const isResultsArea = stepId === 'tutorial-analysis-results';
    const isAnalysisSearch = stepId === 'tutorial-analysis-search';
    const isPanelHandles = stepId === 'tutorial-desk-panel-handles';
    const isDeskIntro = stepId === 'sidebar-nav-desk';
    const isAnalysisIntro = stepId === 'sidebar-nav-analysis';
    const isActivityCalendar = stepId === 'tutorial-desk-activity-calendar';

    let left: number;
    let top: number;
    let transform: string;
    let width: string | undefined;
    let arrowDirection: 'down' | 'up' | 'left' | undefined;

    const getMobileTopCenter = () => ({
        left: positionRect.left + positionRect.width / 2,
        top: positionRect.top - STYLE.TOOLTIP_OFFSET,
        transform: 'translateX(-50%) translateY(-100%)',
        width: 'calc(100vw - 2rem)',
        arrowDirection: 'down' as const,
    });

    if (isResultsArea) {
        // 分析結果は上側に表示（画面内に収まるように調整）
        left = positionRect.left + positionRect.width / 2;
        const topOffset = STYLE.TOOLTIP_OFFSET;
        const minTopMargin = 20; // 画面の上端からの最小マージン
        const estimatedTooltipHeight = 300; // ツールチップの推定高さ（概算）
        
        // 上側に表示した場合の位置
        const topIfAbove = positionRect.top - topOffset;
        
        // 上側に表示すると画面外に出る場合は、下側に表示
        if (topIfAbove - estimatedTooltipHeight < minTopMargin) {
            // 下側に表示
            top = positionRect.bottom + topOffset;
            transform = 'translateX(-50%)';
            arrowDirection = 'up'; // 上向き矢印
        } else {
            // 上側に表示（画面内に収まるように位置を調整）
            top = Math.max(topIfAbove, minTopMargin + estimatedTooltipHeight);
            transform = 'translateX(-50%) translateY(-100%)';
            arrowDirection = 'down'; // 下向き矢印
        }
        
        // 分析結果の案内は幅を広く設定
        width = isMobile ? 'calc(100vw - 2rem)' : 'min(90vw, 1200px)';
    } else if (isMobile && (isSimpleAnalytics || isPanelHandles || isActivityCalendar)) {
        // モバイル時のシンプル分析／パネル操作／アクティビティビューは上側中央に表示
        ({ left, top, transform, width, arrowDirection } = getMobileTopCenter());
    } else if (isSimpleAnalytics || isPanelHandles || isActivityCalendar || ((isDeskIntro || isAnalysisIntro) && !isMobile)) {
        // 右側に表示（デスクトップ）
        left = positionRect.right + STYLE.TOOLTIP_OFFSET;
        top = positionRect.top + positionRect.height / 2;
        transform = 'translateY(-50%)';
    } else if ((isDeskIntro || isAnalysisIntro) && isMobile) {
        // 上側に表示（モバイル・ボトムナビ）
        left = window.innerWidth / 2;
        top = positionRect.top - STYLE.TOOLTIP_OFFSET;
        transform = 'translateX(-50%) translateY(-100%)';
        width = 'calc(100vw - 2rem)';
    } else if (isAnalysisSearch) {
        // 下側に表示
        left = positionRect.left + positionRect.width / 2;
        top = positionRect.bottom + STYLE.TOOLTIP_OFFSET;
        transform = 'translateX(-50%)';
    } else {
        // フォールバック：右側に表示
        left = positionRect.right + STYLE.TOOLTIP_OFFSET;
        top = positionRect.top + positionRect.height / 2;
        transform = 'translateY(-50%)';
    }

    // モバイル時の幅調整（分析結果以外、上側表示の場合のみ）
    if (isMobile && (isDeskIntro || isAnalysisIntro)) {
        width = 'calc(100vw - 2rem)';
        left = window.innerWidth / 2;
    }

    return { left, top, transform, width, arrowDirection };
};

// ============================================================================
// サブコンポーネント
// ============================================================================

interface TutorialArrowProps {
    stepId: string;
    isMobile: boolean;
    arrowDirection?: 'down' | 'up' | 'left';
}

/**
 * ツールチップの矢印コンポーネント
 */
const TutorialArrow: React.FC<TutorialArrowProps> = ({ stepId, isMobile, arrowDirection: propArrowDirection }) => {
    const isSimpleAnalytics = stepId === 'tutorial-desk-simple-analytics';
    const isActivityCalendar = stepId === 'tutorial-desk-activity-calendar';
    const isResultsArea = stepId === 'tutorial-analysis-results';
    const isAnalysisSearch = stepId === 'tutorial-analysis-search';
    const isPanelHandles = stepId === 'tutorial-desk-panel-handles';
    const isDeskIntro = stepId === 'sidebar-nav-desk';
    const isAnalysisIntro = stepId === 'sidebar-nav-analysis';

    const baseStyle = { backgroundColor: STYLE.TOOLTIP_BG_COLOR };
    const borderColor = STYLE.BORDER_COLOR;

    const isMobileDeskIntro = isMobile && isDeskIntro;

    // 矢印の方向とスタイルを決定
    // propArrowDirectionが指定されている場合はそれを使用（分析結果など動的に決定される場合）
    type ArrowDirection = 'down' | 'up' | 'left';
    let direction: ArrowDirection | null = null;

    if (propArrowDirection) {
        direction = propArrowDirection;
    } else {
        // 従来のロジック（stepIdから決定）
        if (isMobile) {
            if (isResultsArea || isDeskIntro || isAnalysisIntro) {
                direction = 'down';
            } else if (isSimpleAnalytics || isActivityCalendar || isPanelHandles) {
                direction = 'left';
            } else {
                direction = 'up';
            }
        } else {
            if (isResultsArea) {
                direction = 'down';
            } else if (isSimpleAnalytics || isPanelHandles || isActivityCalendar || isDeskIntro || isAnalysisIntro) {
                direction = 'left';
            } else if (isAnalysisSearch) {
                direction = 'up';
            }
        }
    }

    if (!direction) return null;

    const arrowStyles: Record<ArrowDirection, { className: string; style: React.CSSProperties }> = {
        down: {
            className: isMobileDeskIntro
                ? 'absolute right-6 bottom-0 translate-y-1/2 w-4 h-4 rotate-45'
                : 'absolute left-1/2 bottom-0 translate-x-1/2 translate-y-1/2 w-4 h-4 rotate-45',
            style: {
                ...baseStyle,
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: `2px solid ${borderColor}`,
                borderBottom: `2px solid ${borderColor}`,
            },
        },
        up: {
            className: 'absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rotate-45',
            style: {
                ...baseStyle,
                borderRight: 'none',
                borderBottom: 'none',
                borderLeft: `2px solid ${borderColor}`,
                borderTop: `2px solid ${borderColor}`,
            },
        },
        left: {
            className: 'absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rotate-45',
            style: {
                ...baseStyle,
                borderTop: 'none',
                borderRight: 'none',
                borderLeft: `2px solid ${borderColor}`,
                borderBottom: `2px solid ${borderColor}`,
            },
        },
    };

    const { className, style } = arrowStyles[direction];
    return <div className={className} style={style} />;
};

interface BookmarkGuideModalProps {
    isOpen: boolean;
    onClose: () => void;
    isMobile: boolean;
}

/**
 * ブックマーク案内モーダル
 */
const BookmarkGuideModal: React.FC<BookmarkGuideModalProps> = ({ isOpen, onClose, isMobile }) => {
    const { t } = useTranslation('onboarding');

    const mobileContent = t('steps.habit.mobile', { returnObjects: true }) as Record<string, string>;
    const desktopContent = t('steps.habit.desktop', { returnObjects: true }) as Record<string, string>;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            contentClassName="w-full max-w-2xl p-8"
        >
            {/* タイトル */}
            <h2 className="text-2xl font-bold mb-4 text-center">{t('steps.habit.title')}</h2>
            
            {/* 紹介文 */}
            <div className="mb-6" dangerouslySetInnerHTML={{ __html: t('steps.habit.recommendation') }} />

            {isMobile ? (
                <div className="space-y-4">
                    {/* ブックマークセクション */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-xl mb-2">{mobileContent.bookmarkHeading}</h3>
                        <p>{mobileContent.bookmarkDescription}</p>
                    </div>
                    {/* アプリインストールセクション */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-xl mb-2">{mobileContent.appHeading}</h3>
                        <h4 className="font-semibold pt-2">{mobileContent.chromeHeading}</h4>
                        <p>{mobileContent.chromeDescription}</p>
                        <h4 className="font-semibold pt-2">{mobileContent.safariHeading}</h4>
                        <p>{mobileContent.safariDescription}</p>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* ブックマークセクション */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-2">{desktopContent.bookmarkHeading}</h3>
                        <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: desktopContent.bookmarkDescription }} />
                    </div>
                    {/* アプリインストールセクション */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-2">{desktopContent.appHeading}</h3>
                        <p className="text-gray-700 mb-1">{desktopContent.chromeHeading}</p>
                        <p className="text-gray-700 mb-3" dangerouslySetInnerHTML={{ __html: desktopContent.chromeDescription }} />
                        {desktopContent.edgeHeading && (
                            <>
                                <p className="text-gray-700 mb-1 font-semibold">{desktopContent.edgeHeading}</p>
                                <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: desktopContent.edgeDescription }} />
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* 閉じるボタン */}
            <div className="mt-8 flex justify-center">
                <Button
                    variant="outline"
                    className="px-8 py-2 border-2 border-black rounded-lg min-w-[120px]"
                    onClick={onClose}
                >
                    {t('steps.habit.cta')}
                </Button>
            </div>
        </Modal>
    );
};

// ============================================================================
// メインコンポーネント
// ============================================================================

const FirstTimeExperienceTutorial: React.FC<FirstTimeExperienceTutorialProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation('onboarding');
    const location = useLocation();
    const navigate = useNavigate();

    // ウィンドウサイズとモバイル判定
    const windowSize = useWindowSize();
    const isMobile = windowSize.width < MOBILE_BREAKPOINT;

    // ステート
    const [globalStep, setGlobalStep] = useTutorialStep();
    const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({});
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
    const [arrowDirection, setArrowDirection] = useState<'down' | 'up' | 'left' | undefined>(undefined);
    const hasScrolledRef = useRef<Record<string, boolean>>({}); // 各ステップでスクロール済みかどうかを記録
    const [showBookmarkModal, setShowBookmarkModal] = useState(false);
    const [activityHasKeyword, setActivityHasKeyword] = useState(false);
    const [simpleGuideRemoved, setSimpleGuideRemoved] = useState(false);
    // 分析実行検知の準備完了フラグ（ページ遷移直後の誤検知を防ぐ）
    const [isReadyForAnalysis, setIsReadyForAnalysis] = useState(false);

    // 現在のページを判定
    const currentPage = useMemo<PageType>(() => {
        if (location.pathname === '/desk') return 'desk';
        if (location.pathname === '/analysis') return 'analysis';
        return 'sidebar';
    }, [location.pathname]);

    // チュートリアルステップの定義
    const allSteps = useMemo<TutorialStep[]>(() => [
        {
            id: 'sidebar-nav-desk',
            title: t('tutorial.steps.desk.title'),
            description: t('tutorial.steps.desk.description'),
            targetId: 'sidebar-nav-desk',
            mobileTargetId: 'bottom-nav-desk',
            page: 'sidebar',
        },
        {
            id: 'tutorial-desk-activity-calendar',
            title: t('tutorial.steps.activityCalendar.title'),
            description: t('tutorial.steps.activityCalendar.description'),
            targetId: 'hmActivityCalendar',
            page: 'desk',
        },
        {
            id: 'tutorial-desk-simple-analytics',
            title: t('tutorial.steps.simpleAnalytics.title'),
            description: t('tutorial.steps.simpleAnalytics.description'),
            targetId: 'hmSimpleAnalytics',
            page: 'desk',
        },
        {
            id: 'sidebar-nav-analysis',
            title: t('tutorial.steps.analysis.title'),
            description: t('tutorial.steps.analysis.description'),
            targetId: 'sidebar-nav-analysis',
            mobileTargetId: 'bottom-nav-analysis',
            page: 'sidebar',
        },
        {
            id: 'tutorial-analysis-search',
            title: t('tutorial.steps.analysisSearch.title'),
            description: t('tutorial.steps.analysisSearch.description'),
            targetId: 'anSearch',
            page: 'analysis',
        },
        {
            id: 'tutorial-analysis-results',
            title: t('tutorial.steps.results.title'),
            description: t('tutorial.steps.results.description'),
            targetId: 'anResults',
            page: 'analysis',
        },
        {
            id: 'tutorial-desk-panel-handles',
            title: t('tutorial.steps.panelHandles.title'),
            description: t('tutorial.steps.panelHandles.description'),
            targetId: 'tutorial-desk-panel-handles',
            page: 'desk',
        },
    ], [t]);

    const currentStepData = allSteps[globalStep];

    // 現在のステップを表示すべきか判定
    const shouldShowStep = useMemo(() => {
        if (!currentStepData) return false;
        if (currentStepData.page === 'sidebar') {
            return currentPage === 'desk';
        }
        return currentStepData.page === currentPage;
    }, [currentStepData, currentPage]);

    // ========================================================================
    // 位置計算
    // ========================================================================

    const updatePosition = useCallback(() => {
        if (!currentStepData || !shouldShowStep) return;

        const isResultsArea = currentStepData.id === 'tutorial-analysis-results';
        const isPanelHandles = currentStepData.id === 'tutorial-desk-panel-handles';

        let highlightRect: RectLike | null = null;

        // ハイライト領域の計算
        if (isPanelHandles) {
            highlightRect = calculateBoundingRect([
                '[data-grid-key="hmActivityCalendar"]',
                '[data-grid-key="hmSimpleAnalytics"]'
            ]);
        } else if (isResultsArea) {
            highlightRect = calculateBoundingRect(
                RESULT_PANEL_KEYS.map(key => `[data-grid-key="${key}"]`)
            );
        } else {
            const targetId = isMobile && currentStepData.mobileTargetId
                ? currentStepData.mobileTargetId
                : currentStepData.targetId;

            const targetElement = document.getElementById(targetId) ||
                document.querySelector(`[data-grid-key="${targetId}"]`);

            if (!targetElement) return;
            highlightRect = targetElement.getBoundingClientRect();
        }

        if (!highlightRect) return;

        // ハイライトスタイルを設定
        const padding = STYLE.HIGHLIGHT_PADDING;
        setHighlightStyle({
            top: highlightRect.top - padding,
            left: highlightRect.left - padding,
            width: highlightRect.width + padding * 2,
            height: highlightRect.height + padding * 2,
        });

        // ツールチップ位置を計算
        const tooltipPos = calculateTooltipPosition(
            highlightRect,
            currentStepData.id,
            isMobile
        );
        const { arrowDirection: calculatedArrowDirection, ...styleProps } = tooltipPos;
        setTooltipStyle(styleProps);
        setArrowDirection(calculatedArrowDirection);

        // ツールチップが画面内に表示されるようにスクロール（最初の表示時のみ）
        const stepId = currentStepData.id;
        if (!hasScrolledRef.current[stepId]) {
            const estimatedTooltipHeight = 300; // ツールチップの推定高さ
            const viewportHeight = window.innerHeight;
            const tooltipTop = styleProps.top as number;
            const tooltipBottom = tooltipTop + estimatedTooltipHeight;
            const margin = 20; // 画面端からのマージン

            // ツールチップが画面外に出ている場合、または画面内に収まっている場合でも中央付近に表示
            let shouldScroll = false;
            let targetScrollY = window.scrollY;

            // ハイライト要素の位置を取得（ページ基準）
            const elementTop = highlightRect.top + window.scrollY;
            const elementBottom = elementTop + highlightRect.height;
            const elementCenter = elementTop + highlightRect.height / 2;

            if (calculatedArrowDirection === 'down') {
                // 上側に表示される場合：ツールチップの上端が画面内に収まるように
                if (tooltipTop < margin) {
                    shouldScroll = true;
                    targetScrollY = elementTop - estimatedTooltipHeight - STYLE.TOOLTIP_OFFSET - margin;
                } else {
                    // 画面内に収まっている場合でも、要素が画面の中央付近に来るようにスクロール
                    const targetElementTop = viewportHeight / 3; // 画面の上から1/3の位置
                    targetScrollY = elementTop - targetElementTop;
                    shouldScroll = true;
                }
            } else if (calculatedArrowDirection === 'up') {
                // 下側に表示される場合：ツールチップの下端が画面内に収まるように
                if (tooltipBottom > viewportHeight - margin) {
                    shouldScroll = true;
                    targetScrollY = elementBottom + STYLE.TOOLTIP_OFFSET + estimatedTooltipHeight - viewportHeight + margin;
                } else {
                    // 画面内に収まっている場合でも、要素が画面の中央付近に来るようにスクロール
                    const targetElementBottom = viewportHeight * 2 / 3; // 画面の上から2/3の位置
                    targetScrollY = elementBottom - targetElementBottom;
                    shouldScroll = true;
                }
            } else {
                // 右側や左側に表示される場合：要素が画面の中央付近に来るようにスクロール
                const targetElementCenter = viewportHeight / 2;
                targetScrollY = elementCenter - targetElementCenter;
                shouldScroll = true;
            }

            if (shouldScroll) {
                window.scrollTo({
                    top: Math.max(0, targetScrollY),
                    behavior: 'smooth'
                });
                hasScrolledRef.current[stepId] = true;
            }
        }
    }, [currentStepData, shouldShowStep, isMobile]);

    // ========================================================================
    // エフェクト
    // ========================================================================

    // ステップ変更時にハイライトと状態をリセット
    useEffect(() => {
        setHighlightStyle({});
        setTooltipStyle({});
        setArrowDirection(undefined);
        // アクティビティカレンダーのステップでない場合はキーワード状態をリセット
        if (currentStepData?.id !== 'tutorial-desk-activity-calendar') {
            setActivityHasKeyword(false);
        }
        // シンプル分析のステップでない場合は案内削除状態をリセット
        if (currentStepData?.id !== 'tutorial-desk-simple-analytics') {
            setSimpleGuideRemoved(false);
        }
    }, [globalStep, currentStepData]);

    // 位置更新
    useEffect(() => {
        if (!isOpen || !shouldShowStep || !currentStepData) return;

        // 初回は少し遅延してから更新（DOMが確実にレンダリングされるのを待つ）
        const delay = currentStepData.page === 'analysis' ? 500 : 200;
        const timeoutId = setTimeout(updatePosition, delay);

        // すべてのステップでリトライ（要素が見つかるまで）
        const retryInterval = setInterval(updatePosition, 300);

        window.addEventListener('resize', updatePosition);
        // スクロールイベントは削除（ツールチップはfixedポジションなので追従不要）

        return () => {
            clearTimeout(timeoutId);
            clearInterval(retryInterval);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, shouldShowStep, currentStepData, updatePosition]);

    // アクティビティビューのキーワード検出
    useEffect(() => {
        // ステップが変わったら常にリセット
        setActivityHasKeyword(false);
        
        if (!isOpen || currentStepData?.id !== 'tutorial-desk-activity-calendar') {
            return;
        }

        const target = document.querySelector('[data-grid-key="hmActivityCalendar"]');
        if (!target) {
            return;
        }

        const detect = () => {
            // キーワードセクションを特定
            // ActivityCalendar.tsxでは、キーワードボタンは以下の構造：
            // <div className="flex flex-wrap justify-center gap-2">
            //    {getVisibleActivities().map((activity, index) => (
            //        <button>{activity.displayName}</button>
            //    ))}
            // </div>
            
            // まず、flex flex-wrap justify-center を持つdivを探す（gap-2は含まれていてもOK）
            let keywordSection = target.querySelector('.flex.flex-wrap.justify-center');
            
            // 見つからない場合は、より広範囲に検索
            if (!keywordSection) {
                keywordSection = target.querySelector('.flex.flex-wrap') ||
                               target.querySelector('[class*="justify-center"]') ||
                               target.querySelector('[class*="flex-wrap"]');
            }
            
            // それでも見つからない場合は、target内のすべてのbutton要素を検索
            // カレンダーのbuttonは react-calendar クラス内にあるので除外
            if (!keywordSection) {
                const allButtons = target.querySelectorAll('button');
                let validKeywordCount = 0;
                allButtons.forEach(btn => {
                    // カレンダー内のボタンは除外
                    const isInCalendar = btn.closest('.react-calendar');
                    if (isInCalendar) {
                        return;
                    }
                    
                    const text = btn.textContent?.trim() || '';
                    // カレンダーの日付ボタン（数字のみ）を除外
                    // キーワードチップは通常、短いテキスト（1-20文字程度）で、数字のみではない
                    if (text.length > 0 && text.length <= 20 && !/^\d+$/.test(text)) {
                        validKeywordCount++;
                    }
                });
                setActivityHasKeyword(validKeywordCount > 0);
                return;
            }
            
            // キーワードセクション内のbutton要素を検出
            // このセクション内のbutton要素はすべてキーワードチップ（カレンダーのbuttonは別の場所にある）
            const keywordButtons = keywordSection.querySelectorAll('button');
            
            // キーワードチップかどうかを判定
            // テキストコンテンツが数字のみでないbutton要素をカウント
            let validKeywordCount = 0;
            keywordButtons.forEach(btn => {
                const text = btn.textContent?.trim() || '';
                // 空でなく、数字のみでない（キーワード名）ものをカウント
                // カレンダーの日付ボタン（数字のみ）を除外
                // キーワード名は通常1-20文字程度
                if (text.length > 0 && text.length <= 20 && !/^\d+$/.test(text)) {
                    validKeywordCount++;
                }
            });
            
            setActivityHasKeyword(validKeywordCount > 0);
        };

        // 初回検出を少し遅延させてDOMが確実にレンダリングされるのを待つ
        const timeoutId = setTimeout(detect, 100);
        
        // 初回検出
        detect();
        
        // MutationObserverで変更を監視
        const observer = new MutationObserver(() => {
            // DOM更新が完了するのを待つ（モーダルが閉じられた後の更新を考慮）
            setTimeout(detect, 200);
        });
        observer.observe(target, { 
            childList: true, 
            subtree: true,
            attributes: true, // クラス属性の変更も監視
            characterData: true // テキストコンテンツの変更も監視
        });
        
        // より頻繁なポーリング（MutationObserverが検知できない場合に備えて）
        // キーワード登録後、モーダルが閉じられてからDOMが更新されるまでの時間を考慮
        const pollInterval = setInterval(detect, 100);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(pollInterval);
            observer.disconnect();
        };
    }, [isOpen, currentStepData]);

    // シンプル分析の初期案内文検出
    useEffect(() => {
        // ステップが変わったら常にリセット
        setSimpleGuideRemoved(false);
        
        if (!isOpen || currentStepData?.id !== 'tutorial-desk-simple-analytics') return;

        const target = document.querySelector('[data-grid-key="hmSimpleAnalytics"]');
        if (!target) return;

        const detect = () => {
            // 初期案内文は以下の条件で表示される：
            // {!setting.id && (
            //     <div className="px-2 pt-2 pb-3 text-center text-gray-500 text-[0.7em]">
            //         {t('panel.defaultNotice.line1')}
            //         <br />
            //         {t('panel.defaultNotice.line2')}
            //     </div>
            // )}
            
            // 初期案内文のテキスト内容を確認
            // 日本語: 「先週」の結果を表示しています。右上の編集アイコンからお好みの条件で設定できます。
            // 英語: Showing results for "Last week". Use the edit icon to adjust the conditions.
            
            const allText = target.textContent || '';
            const lowerText = allText.toLowerCase();
            
            // 初期案内文が存在するかどうかを判定
            // 日本語版のキーワード
            const hasJapaneseGuide = 
                lowerText.includes('先週') && 
                (lowerText.includes('結果を表示') || lowerText.includes('編集アイコン'));
            
            // 英語版のキーワード
            const hasEnglishGuide = 
                (lowerText.includes('showing results') || lowerText.includes('last week')) &&
                (lowerText.includes('edit icon') || lowerText.includes('adjust the conditions'));
            
            // 初期案内文が存在する場合は、まだ削除されていない
            const guideExists = hasJapaneseGuide || hasEnglishGuide;
            
            // 初期案内文が存在しない = 削除されている = 設定が登録された
            setSimpleGuideRemoved(!guideExists);
        };

        // 初回検出を少し遅延させてDOMが確実にレンダリングされるのを待つ
        const timeoutId = setTimeout(detect, 100);
        
        // 初回検出
        detect();
        
        // MutationObserverで変更を監視
        const observer = new MutationObserver(() => {
            // DOM更新が完了するのを待つ（設定登録後の更新を考慮）
            setTimeout(detect, 200);
        });
        observer.observe(target, { 
            childList: true, 
            subtree: true, 
            characterData: true,
            attributes: true // クラス属性の変更も監視
        });
        
        // より頻繁なポーリング（MutationObserverが検知できない場合に備えて）
        // 設定登録後、モーダルが閉じられてからDOMが更新されるまでの時間を考慮
        const pollInterval = setInterval(detect, 100);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(pollInterval);
            observer.disconnect();
        };
    }, [isOpen, currentStepData]);

    // 検索パネルステップに入ったら、一定時間後に検知を開始
    useEffect(() => {
        if (!isOpen || currentStepData?.id !== 'tutorial-analysis-search') {
            setIsReadyForAnalysis(false);
            return;
        }

        // ページ遷移直後の変化を無視するため、1.5秒後に検知開始
        const timerId = setTimeout(() => {
            setIsReadyForAnalysis(true);
        }, 1500);

        return () => {
            clearTimeout(timerId);
            setIsReadyForAnalysis(false);
        };
    }, [isOpen, currentStepData]);

    // 分析実行の検知と自動遷移
    useEffect(() => {
        if (!isOpen || currentStepData?.id !== 'tutorial-analysis-search' || !isReadyForAnalysis) return;
        
        let hasTransitioned = false;
        let initialContent: string | null = null;

        const totalTimePanel = document.querySelector('[data-grid-key="anTotalTime"]');
        if (totalTimePanel) {
            initialContent = totalTimePanel.textContent || '';
        }

        const checkAndTransition = () => {
            if (hasTransitioned) return;

            const panel = document.querySelector('[data-grid-key="anTotalTime"]');
            if (!panel) return;

            const currentContent = panel.textContent || '';

            // コンテンツが変化したら遷移
            if (initialContent !== null && currentContent !== initialContent && currentContent.trim() !== '') {
                hasTransitioned = true;
                observer.disconnect();
                setGlobalStep(prev => Math.min(prev + 1, allSteps.length - 1));
            }
        };

        const observer = new MutationObserver(checkAndTransition);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        return () => observer.disconnect();
    }, [isOpen, currentStepData, isReadyForAnalysis, allSteps.length, setGlobalStep]);

    // ========================================================================
    // イベントハンドラ
    // ========================================================================

    const handleCloseBookmarkModal = useCallback(() => {
        setShowBookmarkModal(false);
        onClose();
    }, [onClose]);

    const handleNext = useCallback(() => {
        if (currentStepData.id === 'sidebar-nav-analysis') {
            const nextStep = globalStep + 1;
            sessionStorage.setItem(STORAGE_KEYS.TUTORIAL_STEP, String(nextStep));
            navigate('/analysis');
        } else if (currentStepData.id === 'tutorial-analysis-results') {
            const nextStep = Math.min(globalStep + 1, allSteps.length - 1);
            sessionStorage.setItem(STORAGE_KEYS.TUTORIAL_STEP, String(nextStep));
            navigate('/desk');
        } else {
            setGlobalStep(prev => Math.min(prev + 1, allSteps.length - 1));
        }
    }, [currentStepData, globalStep, allSteps.length, navigate, setGlobalStep]);

    const handleBack = useCallback(() => {
        if (globalStep > 0) {
            const previousStep = globalStep - 1;
            const prevStepData = allSteps[previousStep];
            const needsPageChange = 
                (currentPage === 'analysis' && prevStepData.page !== 'analysis') ||
                (currentPage === 'desk' && prevStepData.page === 'analysis');
            
            if (needsPageChange) {
                sessionStorage.setItem(STORAGE_KEYS.TUTORIAL_STEP, String(previousStep));
                const targetPath = prevStepData.page === 'analysis' ? '/analysis' : '/desk';
                navigate(targetPath);
            } else {
                setGlobalStep(previousStep);
            }
        }
    }, [globalStep, currentPage, allSteps, navigate, setGlobalStep]);

    const handleComplete = useCallback(() => {
        setShowBookmarkModal(true);
    }, []);

    // ========================================================================
    // レンダリング
    // ========================================================================

    // ブックマークモーダル表示中
    if (showBookmarkModal) {
        return (
            <BookmarkGuideModal
                isOpen={showBookmarkModal}
                onClose={handleCloseBookmarkModal}
                isMobile={isMobile}
            />
        );
    }

    // 早期リターン
    if (!isOpen || !shouldShowStep || !currentStepData) return null;

    const isLast = globalStep === allSteps.length - 1;
    const isFirst = globalStep === 0;
    const isSearchPanelStep = currentStepData.id === 'tutorial-analysis-search';
    const isActivityCalendarStep = currentStepData.id === 'tutorial-desk-activity-calendar';
    const isSimpleAnalyticsStep = currentStepData.id === 'tutorial-desk-simple-analytics';
    
    // 次へボタンの表示条件
    const showNextButton = (() => {
        // 検索パネルステップでは常に非表示
        if (isSearchPanelStep) return false;
        // アクティビティカレンダーステップではキーワードが必要
        if (isActivityCalendarStep) return activityHasKeyword;
        // シンプル分析ステップでは案内が削除されている必要がある
        if (isSimpleAnalyticsStep) return simpleGuideRemoved;
        // その他のステップでは表示
        return true;
    })();

    // ハイライト対象のターゲットID
    const currentTargetId = isMobile && currentStepData.mobileTargetId
        ? currentStepData.mobileTargetId
        : currentStepData.targetId;

    return (
        <>
            {/* チュートリアル中のz-index調整 */}
            <style>{`
                /* サイドバー全体をオーバーレイより下に */
                #sidebar {
                    z-index: 1 !important;
                }
                /* ハイライト対象の要素をオーバーレイより上に */
                #${currentTargetId},
                [data-grid-key="${currentTargetId}"] {
                    position: relative;
                    z-index: ${Z_INDEX.TOOLTIP + 1} !important;
                }
                /* チュートリアル中に開いているモーダルを前面に */
                body > div.fixed.inset-0.z-50,
                body > div[class*="fixed"][class*="inset-0"] {
                    z-index: ${Z_INDEX.TOOLTIP + 1} !important;
                }
            `}</style>

            {/* 検索パネル案内時のドロップダウン用スタイル */}
            {currentStepData.id === 'tutorial-analysis-search' && (
                <style>{`
                    [data-grid-key="anSearch"] .select-container .absolute,
                    [data-grid-key="anSearch"] div[class*="absolute"],
                    [data-grid-key="anSearch"] .relative > div {
                        z-index: ${Z_INDEX.DROPDOWN_MAX} !important;
                    }
                `}</style>
            )}

            {/* オーバーレイレイヤー（4つのdivでハイライト部分を囲む） */}
            {highlightStyle.width && highlightStyle.height && (
                <>
                    {/* 上部オーバーレイ */}
                    <div
                        className="fixed left-0 right-0 top-0 bg-black/60 pointer-events-none"
                        style={{ height: highlightStyle.top, zIndex: Z_INDEX.OVERLAY }}
                    />
                    {/* 下部オーバーレイ */}
                    <div
                        className="fixed left-0 right-0 bottom-0 bg-black/60 pointer-events-none"
                        style={{
                            top: (highlightStyle.top as number) + (highlightStyle.height as number),
                            zIndex: Z_INDEX.OVERLAY,
                        }}
                    />
                    {/* 左部オーバーレイ */}
                    <div
                        className="fixed left-0 bg-black/60 pointer-events-none"
                        style={{
                            top: highlightStyle.top,
                            height: highlightStyle.height,
                            width: highlightStyle.left,
                            zIndex: Z_INDEX.OVERLAY,
                        }}
                    />
                    {/* 右部オーバーレイ */}
                    <div
                        className="fixed right-0 bg-black/60 pointer-events-none"
                        style={{
                            top: highlightStyle.top,
                            height: highlightStyle.height,
                            left: (highlightStyle.left as number) + (highlightStyle.width as number),
                            zIndex: Z_INDEX.OVERLAY,
                        }}
                    />
                    {/* ハイライト枠線 */}
                    <div
                        className="fixed rounded-xl border-2 border-yellow-300 pointer-events-none"
                        style={{ ...highlightStyle, zIndex: Z_INDEX.OVERLAY }}
                    />
                </>
            )}

            {/* ツールチップレイヤー（位置が計算済みの場合のみ表示） */}
            {tooltipStyle.left !== undefined && tooltipStyle.top !== undefined && (
                <div 
                    className={`fixed pointer-events-auto transition-all duration-300 ${
                        currentStepData.id === 'tutorial-analysis-results' 
                            ? 'max-w-3xl' 
                            : 'max-w-sm'
                    } ${isMobile && currentStepData.id !== 'tutorial-analysis-results' ? 'w-[calc(100vw-2rem)]' : ''}`}
                    style={{
                        ...tooltipStyle,
                        zIndex: Z_INDEX.TOOLTIP,
                        // 外側にも光る効果を追加（さらに控えめに）
                        filter: `
                            drop-shadow(0 0 8px rgba(255, 215, 0, 0.10))
                            drop-shadow(0 0 16px rgba(255, 215, 0, 0.06))
                            drop-shadow(0 20px 25px rgba(0, 0, 0, 0.12))
                        `,
                    }}
                >
                    {/* 矢印 */}
                    <TutorialArrow
                        stepId={currentStepData.id}
                        isMobile={isMobile}
                        arrowDirection={arrowDirection}
                    />

                    <div
                        className="rounded-xl overflow-hidden relative"
                        style={{
                            backgroundColor: STYLE.TOOLTIP_BG_COLOR,
                            border: `2px solid ${STYLE.BORDER_COLOR}`,
                            // 後ろから光る効果：さらに控えめに
                            boxShadow: `
                                0 0 10px rgba(255, 215, 0, 0.08),
                                0 0 20px rgba(255, 215, 0, 0.05),
                                0 0 30px rgba(255, 215, 0, 0.03),
                                0 20px 25px -5px rgba(0, 0, 0, 0.1),
                                0 10px 10px -5px rgba(0, 0, 0, 0.04)
                            `,
                        }}
                    >
                        {/* 装飾: 上部のゴールドライン */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-yellow-400 opacity-50" />

                        {/* 背景の装飾文字 */}
                        <div className="absolute -top-4 -right-4 text-6xl font-black text-gray-50 opacity-20 pointer-events-none select-none tracking-widest rotate-12 z-0">
                            GUIDE
                        </div>

                        {/* コンテンツ */}
                        <div className="relative z-10 p-6 flex flex-col gap-4">
                            {/* ヘッダー: タイトルとステップ */}
                            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                    <span className="w-2 h-6 bg-yellow-400 rounded-full inline-block"></span>
                                    {currentStepData.title}
                                </h3>
                                <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                                    {globalStep + 1} / {allSteps.length}
                                </span>
                            </div>

                            {/* 説明文とアクション */}
                            {(() => {
                                const parts = currentStepData.description.split('<br/><br/>');
                                // 説明文: 先頭の絵文字やタグを除去
                                const explanation = parts[0].replace(/^(?:\[.*?\]\s*|📖\s*)/, '');
                                // アクション: 2つ目のブロックがある場合
                                const action = parts.length > 1 ? parts[1].replace(/^(?:\[.*?\]\s*|👉\s*)/, '') : null;

                                return (
                                    <div className="flex flex-col gap-4">
                                        <div 
                                            className="text-gray-600 text-sm leading-relaxed whitespace-pre-line"
                                            dangerouslySetInnerHTML={{ __html: explanation }}
                                        />
                                        
                                        {action && (
                                            <div className="bg-white border border-yellow-200 rounded-lg p-4 relative shadow-sm mt-4">
                                                <div className="absolute -top-2.5 left-3 px-2 bg-[#fffaf0] text-[10px] font-bold text-yellow-600 border border-yellow-200 rounded-full flex items-center gap-1">
                                                    <span>👉</span> TRY IT
                                                </div>
                                                <div 
                                                    className="text-sm text-gray-800 font-medium"
                                                    dangerouslySetInnerHTML={{ __html: action }} 
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            
                            {/* フッター: ボタン類 */}
                            <div className="flex items-center justify-between mt-4 pt-2">
                                {/* 左側: 戻るボタン */}
                                <div>
                                    {!isFirst && (
                                        <button
                                            onClick={handleBack}
                                            className="text-gray-400 hover:text-gray-600 text-xs transition-colors"
                                        >
                                            {t('buttons.back')}
                                        </button>
                                    )}
                                </div>

                                {/* 右側: スキップ または 次へ/完了 */}
                                <div className="flex items-center gap-4">
                                    {/* スキップ (次へボタンがない時のみ) */}
                                    {!isLast && !showNextButton && (
                                        <button
                                            onClick={handleNext}
                                            className="text-gray-400 hover:text-gray-600 text-xs transition-colors"
                                        >
                                            {t('buttons.skip')}
                                        </button>
                                    )}

                                    {/* 次へ / 完了 */}
                                    {isSearchPanelStep ? null : isLast ? (
                                        <button
                                            onClick={handleComplete}
                                            className="px-6 py-2 bg-yellow-400 text-white hover:bg-yellow-500 text-sm font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
                                        >
                                            {t('buttons.complete')}
                                        </button>
                                    ) : showNextButton ? (
                                        <button
                                            onClick={handleNext}
                                            className="px-6 py-2 bg-yellow-400 text-white hover:bg-yellow-500 text-sm font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-1"
                                        >
                                            {t('buttons.next')}
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FirstTimeExperienceTutorial;
